import { rawFileUrl } from './image-source';

/**
 * FRONT-1992: the `site_check_page` tool result — wire twin of
 * `CheckPageResult` (`src/sites/check-page.ts`). Same split as
 * `image-source.ts`'s `GeneratedImageOutput`: no React, no icons, just the
 * shape and the pure reads over it, so `site-check-card.tsx` stays a plain
 * render over already-resolved values.
 */
export interface SiteCheckOutput {
  ok?: boolean;
  path?: string;
  pageUrl?: string;
  status?: number | null;
  title?: string;
  fileId?: string;
  /** The raw-bytes URL the screenshot is served from — the tool always sends
   *  this (`/api/files/<id>/raw`), never a data URI (tool outputs are
   *  persisted into chat messages and the stream tape, so a base64 image
   *  would bloat both). {@link siteCheckImageSrc} still falls back to
   *  deriving one from `fileId` for a wire shape that omitted it. */
  url?: string;
  attachedTo?: { name?: string; kind?: string } | null;
  error?: string;
  code?: string;
}

/** The screenshot's source, same rule as `generatedImageSrc`: prefer the
 *  tool's own `url`, else derive one from `fileId`. Empty string means
 *  "nothing to show". */
export function siteCheckImageSrc(output: SiteCheckOutput): string {
  return output.url ?? (output.fileId ? rawFileUrl(output.fileId) : '');
}

/** A result worth rendering as the specialised card rather than falling
 *  through to the generic tool-call card — a real success (has something to
 *  show) or a named failure, mirroring every other specialised card's own
 *  output-shape guard in `message-bubble.tsx`. */
export function isSiteCheckResultShape(output: SiteCheckOutput): boolean {
  return (output.ok === true && !!siteCheckImageSrc(output)) || !!output.error;
}

/**
 * Badge colour for the page's HTTP status — CLAUDE.md's one-green-one-red
 * rule: 2xx/3xx reads as success, 4xx/5xx as destructive, anything else
 * (no status at all, i.e. the navigate itself never got a response) is
 * neither — the card's own error branch covers that case instead.
 */
export function siteCheckStatusVariant(status: number | null | undefined): 'success-soft' | 'destructive-soft' | null {
  if (typeof status !== 'number') return null;
  if (status >= 200 && status < 400) return 'success-soft';
  if (status >= 400) return 'destructive-soft';
  return null;
}
